/**
 * Shared menu block style resolution for builder canvas and email HTML.
 */
export type MenuButtonStyle = 'text' | 'filled' | 'outline' | 'ghost';
export type MenuButtonShape = 'square' | 'pill';

export const MENU_PILL_BORDER_RADIUS = '999px';
export const MENU_SQUARE_BORDER_RADIUS = '0';

/** Resolve corner radius from buttonShape (pill) or explicit itemBorderRadius (square). */
export const resolveMenuItemBorderRadius = (styles: Record<string, unknown>): string => {
  const shape = styles.buttonShape as MenuButtonShape | undefined;
  if (shape === 'pill') return MENU_PILL_BORDER_RADIUS;
  if (shape === 'square') return MENU_SQUARE_BORDER_RADIUS;
  // Legacy blocks: infer pill from radius, otherwise square
  const radius = styles.itemBorderRadius as string | undefined;
  if (radius === MENU_PILL_BORDER_RADIUS || radius === '9999px') return MENU_PILL_BORDER_RADIUS;
  return radius ?? MENU_SQUARE_BORDER_RADIUS;
};

export const resolveMenuButtonShape = (styles: Record<string, unknown>): MenuButtonShape => {
  const shape = styles.buttonShape as MenuButtonShape | undefined;
  if (shape === 'pill' || shape === 'square') return shape;
  const radius = resolveMenuItemBorderRadius(styles);
  return radius === MENU_PILL_BORDER_RADIUS ? 'pill' : 'square';
};

export type MenuLink = { label?: string; url?: string };

export interface MenuItemComputedStyle {
  color: string;
  fontSize: string;
  fontWeight: string;
  padding: string;
  borderRadius: string;
  border: string;
  backgroundColor: string;
  boxShadow: string;
  textTransform: string;
  letterSpacing: string;
  textDecoration: string;
  whiteSpace: string;
  lineHeight: string;
  verticalAlign: string;
  margin: string;
}

export interface MenuComputedStyles {
  container: {
    padding: string;
    backgroundColor: string;
    textAlign: 'left' | 'center' | 'right';
    borderRadius: string;
    border: string;
    boxShadow: string;
    fontFamily: string;
    fontSize: string;
    fontWeight: string;
  };
  item: MenuItemComputedStyle;
  linkSpacing: string;
  justifyContent: 'flex-start' | 'center' | 'flex-end';
}

export const menuMobile = {
  padding: '14px 16px',
  fontSize: '12px',
  linkSpacing: '6px',
  itemPadding: '7px 10px',
} as const;

/** Even gap around each button (horizontal + wrapped rows) without shifting content upward. */
export const resolveMenuItemMargin = (spacingPx: number, isMobile: boolean): string => {
  const gap = isMobile ? 4 : Math.max(4, Math.round(spacingPx / 2));
  return `${gap}px`;
};

const parseSpacingPx = (value: string | undefined, fallback: number): number => {
  const n = parseInt(String(value || '').replace(/px/gi, ''), 10);
  return Number.isFinite(n) ? n : fallback;
};

export const applyMenuButtonStyle = (
  buttonStyle: MenuButtonStyle,
  styles: Record<string, unknown>
): Pick<MenuItemComputedStyle, 'backgroundColor' | 'border' | 'boxShadow'> => {
  const itemBg = (styles.itemBackgroundColor as string) || 'transparent';
  const itemBorderColor = styles.itemBorderColor as string | undefined;
  const itemShadow = (styles.itemShadow as string) || 'none';

  switch (buttonStyle) {
    case 'text':
      return { backgroundColor: 'transparent', border: 'none', boxShadow: 'none' };
    case 'outline':
      return {
        backgroundColor: 'transparent',
        border: itemBorderColor ? `1px solid ${itemBorderColor}` : '1px solid #CBD5E1',
        boxShadow: 'none',
      };
    case 'ghost':
      return {
        backgroundColor: itemBg !== 'transparent' ? itemBg : '#F1F5F9',
        border: 'none',
        boxShadow: 'none',
      };
    case 'filled':
    default:
      return {
        backgroundColor: itemBg,
        border: itemBorderColor ? `1px solid ${itemBorderColor}` : 'none',
        boxShadow: itemShadow,
      };
  }
};

export const resolveMenuStyles = (
  styles: Record<string, unknown> = {},
  globalFontFamily?: string,
  isMobile = false
): MenuComputedStyles => {
  const m = menuMobile;
  const buttonStyle = (styles.buttonStyle as MenuButtonStyle) || 'filled';
  const align = (styles.textAlign as string) || 'center';
  const textAlign: 'left' | 'center' | 'right' =
    align === 'left' ? 'left' : align === 'right' ? 'right' : 'center';

  const justifyContent: MenuComputedStyles['justifyContent'] =
    textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center';

  const basePadding = (styles.padding as string) || '16px 20px';
  const spacingRaw = (styles.linkSpacing as string) || '10px';
  const spacingNum = parseSpacingPx(spacingRaw, 10);
  const linkSpacing = isMobile ? `${Math.max(4, Math.round(spacingNum * 0.65))}px` : spacingRaw;

  const itemPadding = isMobile
    ? ((styles.itemPaddingMobile as string) || m.itemPadding)
    : ((styles.itemPadding as string) || '8px 14px');

  const fontSize = isMobile
    ? ((styles.fontSizeMobile as string) || m.fontSize)
    : ((styles.fontSize as string) || '14px');

  const itemStyle = applyMenuButtonStyle(buttonStyle, styles);

  const containerBorder = styles.containerBorderColor
    ? `1px solid ${styles.containerBorderColor as string}`
    : ((styles.border as string) || 'none');

  return {
    container: {
      padding: isMobile ? m.padding : basePadding,
      backgroundColor: (styles.backgroundColor as string) || 'transparent',
      textAlign,
      borderRadius: (styles.containerBorderRadius as string) ?? (styles.borderRadius as string) ?? '0',
      border: containerBorder,
      boxShadow: (styles.containerShadow as string) || 'none',
      fontFamily: (styles.fontFamily as string) || globalFontFamily || 'Arial, sans-serif',
      fontSize,
      fontWeight: (styles.fontWeight as string) || '600',
    },
    item: {
      color: (styles.itemTextColor as string) || (styles.color as string) || '#334155',
      fontSize,
      fontWeight: (styles.fontWeight as string) || '600',
      padding: itemPadding,
      borderRadius: resolveMenuItemBorderRadius(styles),
      textTransform: (styles.textTransform as string) || 'none',
      letterSpacing: (styles.letterSpacing as string) || '0',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      lineHeight: '1.25',
      verticalAlign: 'middle',
      margin: resolveMenuItemMargin(spacingNum, isMobile),
      ...itemStyle,
    },
    linkSpacing,
    justifyContent,
  };
};
